// Validación: una obligación no puede tener texto "huérfano" (acciones realizadas
// o descripción de evidencia) sin al menos una IMAGEN cargada. La evidencia
// fotográfica es obligatoria; si no hay imagen, la obligación debe quedar
// totalmente vacía (el PDF pondrá el texto por defecto "no se ejecutaron acciones").

const _tieneAcciones = (obl) => (obl.acciones_realizadas || '').trim().length > 0;
const _tieneDescEvidencia = (obl) => (obl.evidencias || []).some(e => (e.descripcion || '').trim());
const _tieneTexto = (obl) => _tieneAcciones(obl) || _tieneDescEvidencia(obl);
const _tieneImagen = (obl) => (obl.evidencias || []).some(e => e.imagen_url);

// Dónde está el texto huérfano, para orientar al usuario.
const _dondeTexto = (obl) => {
  const partes = [];
  if (_tieneAcciones(obl)) partes.push('Acciones realizadas');
  if (_tieneDescEvidencia(obl)) partes.push('descripción de evidencia');
  return partes.join(' y ');
};

/**
 * Devuelve las obligaciones que tienen texto pero NINGUNA imagen cargada.
 * (Las que están vacías o las que tienen al menos una imagen son válidas.)
 */
export const obligacionesConTextoSinImagen = (obligaciones = []) =>
  obligaciones.filter(obl => _tieneTexto(obl) && !_tieneImagen(obl));

/**
 * Si hay obligaciones con texto pero sin imagen, retorna un mensaje de error
 * que indica CLARAMENTE en cuál obligación está el texto huérfano (número,
 * título y dónde), para saber dónde ir a corregir. Si todo está bien, null.
 */
export const validarObligacionesConImagen = (obligaciones = []) => {
  const invalidas = obligacionesConTextoSinImagen(obligaciones);
  if (invalidas.length === 0) return null;

  if (invalidas.length === 1) {
    const o = invalidas[0];
    const titulo = o.titulo ? ` («${o.titulo.slice(0, 45)}${o.titulo.length > 45 ? '…' : ''}»)` : '';
    return `No puedes guardar: la Obligación ${o.numero}${titulo} tiene texto en "${_dondeTexto(o)}" pero ` +
      `no tiene ninguna imagen cargada. Ve a esa obligación y carga al menos una imagen (o borra el texto).`;
  }
  const numeros = invalidas.map(o => o.numero).join(', ');
  return `No puedes guardar: las Obligaciones ${numeros} tienen texto pero no tienen ninguna imagen cargada. ` +
    `Ve a cada una y carga al menos una imagen (o borra el texto).`;
};

// ─── Validación: evidencias con fecha ANTERIOR al mes de la cuenta ───────────
// Caso típico: al duplicar una cuenta, las evidencias clonadas conservan la
// fecha de carga del mes pasado y no corresponden al mes del nuevo informe.

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// "Junio de 2026" -> Date(2026, 5, 1). null si no se puede parsear.
const _inicioDelMes = (mesCobro) => {
  if (!mesCobro || !mesCobro.includes(' de ')) return null;
  const [mesStr, anioStr] = mesCobro.split(' de ');
  const mi = MESES.indexOf((mesStr || '').trim().toLowerCase());
  const anio = parseInt((anioStr || '').trim(), 10);
  if (mi === -1 || Number.isNaN(anio)) return null;
  return new Date(anio, mi, 1, 0, 0, 0, 0);
};

// fecha_creacion viene como "DD/MM/YYYY HH:MM:SS" (o ISO en datos antiguos).
const _fechaEvidencia = (fc) => {
  if (!fc) return null;
  if (typeof fc === 'string' && fc.includes('/')) {
    const [d, m, y] = fc.split(' ')[0].split('/').map(n => parseInt(n, 10));
    if (!d || !m || !y) return null;
    return new Date(y, m - 1, d);
  }
  const dt = new Date(fc);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const _soloFecha = (fc) => (typeof fc === 'string' && fc.includes('/')) ? fc.split(' ')[0] : fc;

/** Evidencias cargadas ANTES del primer día del mes de la cuenta. */
export const evidenciasAnterioresAlMes = (obligaciones = [], mesCobro = '') => {
  const inicio = _inicioDelMes(mesCobro);
  if (!inicio) return []; // sin mes válido no validamos
  const fuera = [];
  (obligaciones || []).forEach(obl => {
    (obl.evidencias || []).forEach((ev, idx) => {
      const f = _fechaEvidencia(ev.fecha_creacion);
      if (f && f < inicio) {
        fuera.push({ numero: obl.numero, evNum: idx + 1, fecha: _soloFecha(ev.fecha_creacion) });
      }
    });
  });
  return fuera;
};

/**
 * Si hay evidencias con fecha anterior al mes de la cuenta, retorna un mensaje
 * indicando en qué obligación y cuáles. Si todo está bien, null.
 */
export const validarEvidenciasDelMes = (obligaciones = [], mesCobro = '') => {
  const fuera = evidenciasAnterioresAlMes(obligaciones, mesCobro);
  if (fuera.length === 0) return null;

  const porObl = {};
  fuera.forEach(f => {
    if (!porObl[f.numero]) porObl[f.numero] = [];
    porObl[f.numero].push(`${f.evNum} (${f.fecha})`);
  });
  const lineas = Object.entries(porObl)
    .map(([num, evs]) => `• Obligación ${num}: evidencia${evs.length > 1 ? 's' : ''} ${evs.join(', ')}`);
  // No mostrar un muro de texto: máximo 6 obligaciones detalladas
  let detalle = lineas.slice(0, 6).join('\n');
  if (lineas.length > 6) detalle += `\n… y ${lineas.length - 6} obligación(es) más`;

  const plural = fuera.length > 1;
  return `Hay ${fuera.length} evidencia${plural ? 's' : ''} con fecha de carga ANTERIOR al mes de la cuenta ` +
    `(${mesCobro}). No corresponden a este informe: reemplaza la imagen, elimínala${plural ? 's' : ''} ` +
    `manualmente o usa el botón de abajo.\n${detalle}`;
};

/**
 * Elimina de las obligaciones las evidencias con fecha de carga anterior al mes
 * de la cuenta. Si una obligación queda SIN imágenes, también limpia sus
 * "Acciones Realizadas" y texto de evidencias (para no dejar texto huérfano);
 * si le quedan evidencias válidas del mes, sus acciones se conservan.
 * Retorna { obligaciones, eliminadas }.
 */
export const eliminarEvidenciasAnterioresAlMes = (obligaciones = [], mesCobro = '') => {
  const inicio = _inicioDelMes(mesCobro);
  if (!inicio) return { obligaciones, eliminadas: 0 };
  let eliminadas = 0;
  const nuevas = (obligaciones || []).map(obl => {
    const evs = obl.evidencias || [];
    const conservadas = evs.filter(ev => {
      const f = _fechaEvidencia(ev.fecha_creacion);
      const esVieja = f && f < inicio;
      if (esVieja) eliminadas++;
      return !esVieja;
    });
    if (conservadas.length === evs.length) return obl;
    const quedaSinImagenes = !conservadas.some(e => e.imagen_url);
    return {
      ...obl,
      evidencias: conservadas,
      acciones_realizadas: quedaSinImagenes ? '' : obl.acciones_realizadas,
      texto_evidencias: quedaSinImagenes ? '' : obl.texto_evidencias,
      acciones_evidencias_ids: quedaSinImagenes ? [] : obl.acciones_evidencias_ids,
    };
  });
  return { obligaciones: nuevas, eliminadas };
};

// ─── Detección: evidencias NUEVAS agregadas después de escribir las acciones ─
// Cada obligación guarda en `acciones_evidencias_ids` la "foto" de las
// evidencias que existían cuando se escribieron/generaron sus acciones
// realizadas. Toda evidencia con imagen que no esté en esa foto es "nueva" y
// las acciones probablemente no la incluyen.

/** IDs de las evidencias con imagen de una obligación (para la "foto"). */
export const idsEvidenciasConImagen = (obl) =>
  (obl.evidencias || []).filter(e => e.imagen_url).map(e => e.id);

/**
 * Devuelve [{numero, titulo, nuevas: [númerosDeEvidencia], removidas: N}] para
 * las obligaciones con acciones escritas cuyas evidencias CAMBIARON después:
 *  - nuevas: evidencias con imagen que no estaban en la foto (agregadas/movidas aquí)
 *  - removidas: cuántas evidencias de la foto ya no existen (eliminadas o
 *    movidas a otra obligación) — las acciones pueden estar describiéndolas.
 * Sin foto (datos antiguos) no se detecta — los editores la inicializan al cargar.
 */
export const obligacionesConNuevasEvidencias = (obligaciones = []) =>
  (obligaciones || []).map(obl => {
    const acciones = (obl.acciones_realizadas || '').trim();
    if (!acciones) return null; // sin acciones: lo cubre la validación de faltantes
    if (!Array.isArray(obl.acciones_evidencias_ids)) return null; // sin foto: no detectar
    const cubiertas = new Set(obl.acciones_evidencias_ids);
    const idsActuales = new Set((obl.evidencias || []).map(e => e.id));
    const nuevas = (obl.evidencias || [])
      .map((e, idx) => ({ e, num: idx + 1 }))
      .filter(({ e }) => e.imagen_url && !cubiertas.has(e.id))
      .map(({ num }) => num);
    const removidas = (obl.acciones_evidencias_ids || []).filter(id => !idsActuales.has(id)).length;
    if (nuevas.length === 0 && removidas === 0) return null;
    return { numero: obl.numero, titulo: obl.titulo, nuevas, removidas };
  }).filter(Boolean);

// ─── Validación: evidencias cargadas SIN "Acciones Realizadas" ───────────────
// Las acciones realizadas son el resumen de las evidencias de la obligación:
// si hay imágenes cargadas, el campo no puede quedar vacío.

/** Obligaciones con al menos una imagen cargada pero acciones_realizadas vacío. */
export const obligacionesSinAcciones = (obligaciones = []) =>
  (obligaciones || []).filter(obl =>
    (obl.evidencias || []).some(e => e.imagen_url) &&
    (obl.acciones_realizadas || '').trim().length === 0
  );

/** Cuántas evidencias (con imagen) de la obligación NO tienen descripción. */
export const evidenciasSinDescripcion = (obl) =>
  (obl.evidencias || []).filter(e => e.imagen_url && !(e.descripcion || '').trim()).length;

/**
 * Mensaje genérico de bloqueo cuando hay obligaciones con evidencias pero sin
 * acciones realizadas. null si todo está bien.
 */
export const validarAccionesRealizadas = (obligaciones = []) => {
  const faltan = obligacionesSinAcciones(obligaciones);
  if (faltan.length === 0) return null;
  if (faltan.length === 1) {
    const o = faltan[0];
    const titulo = o.titulo ? ` («${o.titulo.slice(0, 45)}${o.titulo.length > 45 ? '…' : ''}»)` : '';
    return `La Obligación ${o.numero}${titulo} tiene evidencias cargadas pero el campo ` +
      `"Acciones Realizadas" está vacío. Escríbelas antes de continuar.`;
  }
  const numeros = faltan.map(o => o.numero).join(', ');
  return `Las Obligaciones ${numeros} tienen evidencias cargadas pero el campo ` +
    `"Acciones Realizadas" está vacío. Escríbelas antes de continuar.`;
};
